import React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { Primary } from "../Buttons/Button.stories";
import PropTypes from "prop-types";
import { useLocation } from "react-router";

/**
 * StatusActionButton component for rendering a primary button with dynamic action
 * @param {Object} props - Props for the StatusActionButton component
 * @param {Object} row - Row data for the current table row
 * @param {Function} onSubmit - Submit handler function
 * @returns {JSX.Element} - Rendered button component
 */
const StatusActionButton = ({ row, onSubmit }) => {
  return (
    <Primary
      label={"Search"}
      onClick={() => onSubmit(row)}
      size=""
      variant={row.status.variant}
      className="w-100"
    />
  );
};

StatusActionButton.propTypes = {
  row: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * Creates a standardized column definition for the table
 * @param {string} dataField - Field name in data object
 * @param {string} text - Display text for column header
 * @param {boolean} sort - Whether column is sortable
 * @param {boolean} search - Whether column is searchable
 * @param {string} placeholder - Placeholder text for search filter
 * @returns {Object} Column configuration object
 */
const createColumn = ({
  dataField,
  text,
  sort = false,
  search = false,
  placeholder = "",
  customFilter = null,
  formatter = null,
  headerClass = "",
  cellClass ="",
  style = {},
}) => {
  const column = {
    dataField,
    text,
    sort,
    style
  };

  if (search) {
    column.filter = textFilter({
      placeholder: placeholder || `Search by ${dataField}`,
      ...(customFilter && { onFilter: customFilter }),
    });
  }

  if (formatter) {
    column.formatter = formatter;
  }

  if (headerClass) {
    column.headerClasses = headerClass;
  }

  if (cellClass) {
    column.classes = cellClass; // <td>
  }

  return column;
};

/**
 * DuplicateIdTable component renders a table with customizable features such as sorting, searching, and pagination.
 * @param {Object} props - Props for the DuplicateIdTable component.
 * @param {Array} props.data - Data to be displayed in the table.
 * @param {boolean} props.sort - Flag indicating if sorting is enabled.
 * @param {boolean} props.search - Flag indicating if searching is enabled.
 * @param {boolean} props.pagination - Flag indicating if pagination is enabled.
 * @param {Function} props.onSubmit - Function to handle row submission
 * @returns {JSX.Element} - Rendered component.
 */
const DuplicateIdTable = ({ data, sort, search, pagination, onSubmit }) => {
  const location = useLocation();
  const isClinicalService = location.state?.serviceType === "clinical";
  /* Custom filter function for the status column */
  const statusFilter = (filterVal, data) => {
    if (!filterVal) {
      return data;
    }

    return data.filter((row) =>
      row.status.raw.toLowerCase().includes(filterVal.toLowerCase())
    );
  };

  // Define formatter function outside of the columns definition
  const actionFormatter = (cell, row) => (
    <StatusActionButton row={row} onSubmit={onSubmit} />
  );

  const defaultCellStyle = {
    whiteSpace: "normal",
    wordBreak: "break-word",
    maxWidth: "180px", 
    width: "fit-content"
  };

  const defaultCellStyleforEmail={
    whiteSpace: "normal",
    wordBreak: "break-word",
    minWidth: "250px", 
    width: "fit-content"
  }

  /* DuplicateIdTable columns configuration */
  const columns = [
    createColumn({
      dataField: "bookingId",
      text: "Booking ID",
      sort,
      search,
      placeholder: "Search by bookingId",
      headerClass: "column-heading-name",
      style: defaultCellStyle,
    }),
    createColumn({
      dataField: "first_name",
      text: "First name",
      sort,
      search,
      placeholder: "Search by testType",
      headerClass: "column-heading-name",
      style: defaultCellStyle,
    }),
    createColumn({
      dataField: "last_name",
      text: "Last name",
      sort,
      search,
      placeholder: "Search by sampleType",
      headerClass: "column-heading-name",
      style: defaultCellStyle,
    }),
    createColumn({
      dataField: "dob",
      text: "Date of birth",
      sort,
      search,
      placeholder: "Search by reportedDate",
      style: defaultCellStyle,
    }),
    createColumn({
      dataField: "email",
      text: "Email",
      sort,
      search,
      placeholder: "Search by doctor",
      headerClass: "email-cell", 
      style: defaultCellStyleforEmail,
    }),
    createColumn({
      dataField: "status",
      text: "Options",
      sort,
      search,
      placeholder: "Search by status",
      customFilter: statusFilter,
      formatter: actionFormatter,
      style: defaultCellStyle,
    }),
  ];

  const clinicalColumns = [
    createColumn({ dataField: "id", text: "User id", sort, search }),
    createColumn({ dataField: "patient_id", text: "Patient id", sort, search }),
    createColumn({ dataField: "first_name", text: "First name", sort, search }),
    createColumn({ dataField: "last_name", text: "Last name", sort, search }),
    createColumn({ dataField: "date_of_birth", text: "D.O.B", sort, search }),
    createColumn({
      dataField: "status",
      text: "Options",
      sort,
      search,
      customFilter: statusFilter,
      formatter: actionFormatter,
    }),
  ];

  /* DuplicateIdTable pagination options */
  const options = {
    sizePerPage: 4, // Number of records per page
    pageStartIndex: 1, // First page index
    paginationSize: 1, // Number of pagination navigation items (pages)
    paginationTotalRenderer: () => null, // Render nothing for total
    prePageText: "<", // Customize the text for the previous page button
    nextPageText: ">", // Customize the text for the next page button
    totalSize: data.length, // Total number of records
    showTotal: true, // Show the total number of records
    alwaysShowAllBtns: true, // Always show all pagination buttons (first, last, prev, next)
    withFirstAndLast: false, // Do not show first and last buttons
    hideSizePerPage: true, // Hide the dropdown for selecting the number of records per page
    // Add a function to conditionally apply classes to rows
    rowClasses: (data) => {
      return data.status.raw === "registered" ? "registered" : "";
    },
  };

  /* Render the Bootstrap table component with provided data, columns, filters, and pagination */
  return (
    <BootstrapTable
      columns={isClinicalService ? clinicalColumns : columns} // Configuration of table columns
      data={data} // Data to be displayed in the table
      filter={search && filterFactory()} // Enable filtering if the search flag is true
      keyField="id" // Unique key field for each row
      pagination={pagination && paginationFactory(options)} // Enable pagination if the pagination flag is true
    />
  );
};

DuplicateIdTable.propTypes = {
  data: PropTypes.array.isRequired,
  sort: PropTypes.bool,
  search: PropTypes.bool,
  pagination: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

export default DuplicateIdTable;
